import React, { useEffect, useState } from 'react'
import MainLayout from '../../components/MainLayout';
import {
    Card,
    Select,
    Row,
    Col,
    Typography,
    Space,
    Alert,
    message,
    Tabs,
    Input,
    Button,
    Switch,
} from 'antd';
import {
    SettingOutlined,
    KeyOutlined,
    InfoCircleOutlined,
    ApiOutlined,
    SafetyOutlined,
    CheckCircleOutlined,
    GlobalOutlined,
} from '@ant-design/icons';
import { ApiConfig } from '../../services/ApiConfigs';
import { ApiServices } from '../../services/apiServices';
import StoreApiKeys from '../../components/StoreApiKeys';
import SignatureTester from '../../components/SignatureTester';

const { Title, Text, Paragraph } = Typography;

const ASSIGNMENT_MODE_KEY = 'Shipment Assignment Mode';
const GOOGLE_MAPS_KEY = 'Google Maps API Key';
const GOOGLE_MAPS_ENABLED_KEY = 'Google Maps Enabled';

const assignmentModeOptions = [
    {
        value: 'admin_assign',
        label: 'Admin assigns driver',
        description: 'Admin selects and assigns a driver to each order/shipment. Driver receives the assignment directly.'
    },
    {
        value: 'driver_accept',
        label: 'Driver accepts shipment',
        description: 'Admin creates a shipment; drivers see available shipments and can accept them. You can switch to admin assign anytime to assign directly.'
    },
];

const Setting = () => {
    const [assignmentMode, setAssignmentMode] = useState('admin_assign');
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('general');
    const [googleMapsKey, setGoogleMapsKey] = useState('');
    const [googleMapsKeySaving, setGoogleMapsKeySaving] = useState(false);
    const [googleMapsKeyInput, setGoogleMapsKeyInput] = useState('');
    const [googleMapsEnabled, setGoogleMapsEnabled] = useState(false);
    const [googleMapsEnabledSaving, setGoogleMapsEnabledSaving] = useState(false);

    useEffect(() => {
        fetchAllConfigs();
    }, []);

    const fetchAllConfigs = async () => {
        try {
            setLoading(true);
            const res = await ApiServices('get', ApiConfig.APP_CONFIG);
            const list = res?.data?.data || [];
            const assignRow = list.find((r) => r.Key === ASSIGNMENT_MODE_KEY);
            if (assignRow && (assignRow.value === 'admin_assign' || assignRow.value === 'driver_accept')) {
                setAssignmentMode(assignRow.value);
            }
            const mapsRow = list.find((r) => r.Key === GOOGLE_MAPS_KEY);
            if (mapsRow) {
                setGoogleMapsKey(mapsRow.value || '');
                setGoogleMapsKeyInput(mapsRow.value || '');
            }
            const enabledRow = list.find((r) => r.Key === GOOGLE_MAPS_ENABLED_KEY);
            if (enabledRow) setGoogleMapsEnabled(enabledRow.value === 'true');
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    const getAssignmentMode = fetchAllConfigs;

    const handleAssignmentModeChange = async (value) => {
        try {
            setLoading(true);
            setAssignmentMode(value);
            const body = { Key: ASSIGNMENT_MODE_KEY, value };
            const res = await ApiServices('post', ApiConfig.APP_CONFIG, body);
            if (res?.success || res?.status === 200) {
                message.success('Shipment assignment setting updated successfully');
            } else {
                message.error('Failed to update setting');
            }
        } catch (error) {
            console.log(error);
            message.error('Failed to update setting');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveGoogleMapsKey = async () => {
        try {
            setGoogleMapsKeySaving(true);
            const body = { Key: GOOGLE_MAPS_KEY, value: googleMapsKeyInput.trim() };
            const res = await ApiServices('post', ApiConfig.APP_CONFIG, body);
            if (res?.success || res?.status === 200) {
                setGoogleMapsKey(googleMapsKeyInput.trim());
                message.success('Google Maps API key saved');
            } else {
                message.error('Failed to save key');
            }
        } catch (error) {
            message.error('Failed to save key');
        } finally {
            setGoogleMapsKeySaving(false);
        }
    };

    const handleGoogleMapsToggle = async (checked) => {
        try {
            setGoogleMapsEnabledSaving(true);
            setGoogleMapsEnabled(checked);
            await ApiServices('post', ApiConfig.APP_CONFIG, { Key: GOOGLE_MAPS_ENABLED_KEY, value: String(checked) });
            message.success(`Google Maps ${checked ? 'enabled' : 'disabled'}`);
        } catch {
            message.error('Failed to update setting');
            setGoogleMapsEnabled(!checked);
        } finally {
            setGoogleMapsEnabledSaving(false);
        }
    };

    const selectedAssignmentOption = assignmentModeOptions.find((o) => o.value === assignmentMode);

    const tabItems = [
        {
            key: 'general',
            label: (
                <Space>
                    <SettingOutlined />
                    <span>General</span>
                </Space>
            ),
            children: (
                <div>
                    <Card style={{ marginBottom: 24 }} variant="borderless">
                        <Space direction="vertical" size="large" style={{ width: '100%' }}>
                            <div>
                                <Title level={4} style={{ marginBottom: 16 }}>
                                    <Space>
                                        <SafetyOutlined style={{ color: '#1890ff' }} />
                                        <span>Shipment assignment</span>
                                    </Space>
                                </Title>
                                <Paragraph style={{ marginBottom: 24, color: '#595959', fontSize: 14 }}>
                                    Choose how drivers are assigned to orders: either you assign drivers yourself, or drivers can accept available shipments.
                                </Paragraph>
                                <div style={{
                                    marginBottom: 16,
                                    padding: 20,
                                    background: '#fafafa',
                                    borderRadius: 8,
                                    border: '1px solid #f0f0f0'
                                }}>
                                    <Text strong style={{ display: 'block', marginBottom: 16, fontSize: 15 }}>
                                        Assignment mode
                                    </Text>
                                    <Select
                                        value={assignmentMode}
                                        onChange={handleAssignmentModeChange}
                                        style={{ width: '100%', minWidth: 300 }}
                                        placeholder="Select mode"
                                        size="large"
                                        loading={loading}
                                        optionLabelProp="label"
                                    >
                                        {assignmentModeOptions.map((s) => (
                                            <Select.Option key={s.value} value={s.value} label={s.label}>
                                                <div style={{ padding: '4px 0' }}>
                                                    <div style={{ fontWeight: 500, fontSize: 14, lineHeight: '22px', marginBottom: 4 }}>
                                                        {s.label}
                                                    </div>
                                                    <div style={{ fontSize: 13, color: '#595959', lineHeight: '20px', marginTop: 4 }}>
                                                        {s.description}
                                                    </div>
                                                </div>
                                            </Select.Option>
                                        ))}
                                    </Select>
                                    {selectedAssignmentOption && (
                                        <div style={{ marginTop: 12 }}>
                                            <Text style={{ fontSize: 13, color: '#595959' }}>
                                                <InfoCircleOutlined style={{ marginRight: 6, color: '#1890ff' }} />
                                                {selectedAssignmentOption.description}
                                            </Text>
                                        </div>
                                    )}
                                </div>
                                <Alert
                                    message="You can change this anytime"
                                    description="Switching to &quot;Admin assigns driver&quot; lets you assign drivers directly. Switching to &quot;Driver accepts shipment&quot; lets drivers see and accept available shipments."
                                    type="info"
                                    icon={<InfoCircleOutlined />}
                                    showIcon
                                    style={{ marginTop: 16 }}
                                />
                            </div>

                            <div>
                                <Title level={4} style={{ marginBottom: 16 }}>
                                    <Space>
                                        <GlobalOutlined style={{ color: '#1890ff' }} />
                                        <span>Map Settings</span>
                                    </Space>
                                </Title>
                                <div style={{
                                    padding: 20,
                                    background: '#fafafa',
                                    borderRadius: 8,
                                    border: '1px solid #f0f0f0'
                                }}>
                                    {/* Toggle row */}
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        marginBottom: googleMapsEnabled ? 20 : 0,
                                        paddingBottom: googleMapsEnabled ? 16 : 0,
                                        borderBottom: googleMapsEnabled ? '1px solid #e8e8e8' : 'none',
                                    }}>
                                        <div>
                                            <Text strong style={{ fontSize: 15, display: 'block' }}>Use Google Maps</Text>
                                            <Text style={{ fontSize: 13, color: '#595959' }}>
                                                {googleMapsEnabled
                                                    ? 'Google Maps is active for all map views'
                                                    : 'OpenStreetMap (free) is currently active'}
                                            </Text>
                                        </div>
                                        <Switch
                                            checked={googleMapsEnabled}
                                            onChange={handleGoogleMapsToggle}
                                            loading={googleMapsEnabledSaving}
                                            disabled={!googleMapsKey}
                                            checkedChildren="ON"
                                            unCheckedChildren="OFF"
                                        />
                                    </div>

                                    {/* API Key input — always visible */}
                                    {googleMapsEnabled && (
                                        <div>
                                            <Text strong style={{ display: 'block', marginBottom: 10, fontSize: 14 }}>
                                                Google Maps API Key
                                            </Text>
                                            <Space.Compact style={{ width: '100%' }}>
                                                <Input.Password
                                                    value={googleMapsKeyInput}
                                                    onChange={(e) => setGoogleMapsKeyInput(e.target.value)}
                                                    placeholder="AIza..."
                                                    size="large"
                                                    style={{ flex: 1 }}
                                                />
                                                <Button
                                                    type="primary"
                                                    size="large"
                                                    loading={googleMapsKeySaving}
                                                    onClick={handleSaveGoogleMapsKey}
                                                    disabled={googleMapsKeyInput === googleMapsKey}
                                                >
                                                    Save
                                                </Button>
                                            </Space.Compact>
                                            {googleMapsKey && (
                                                <Text style={{ fontSize: 13, color: '#52c41a', display: 'block', marginTop: 10 }}>
                                                    <CheckCircleOutlined style={{ marginRight: 6 }} />
                                                    Key saved
                                                </Text>
                                            )}
                                        </div>
                                    )}

                                    {/* Prompt to add key if switch is off */}
                                    {!googleMapsEnabled && (
                                        <div style={{ marginTop: 12 }}>
                                            <Text style={{ fontSize: 13, color: '#595959' }}>
                                                <InfoCircleOutlined style={{ marginRight: 6, color: '#1890ff' }} />
                                                {googleMapsKey
                                                    ? 'Key saved — turn the switch ON to activate Google Maps'
                                                    : 'Add an API key below, then enable the switch'}
                                            </Text>
                                            <div style={{ marginTop: 12 }}>
                                                <Space.Compact style={{ width: '100%' }}>
                                                    <Input.Password
                                                        value={googleMapsKeyInput}
                                                        onChange={(e) => setGoogleMapsKeyInput(e.target.value)}
                                                        placeholder="AIza..."
                                                        size="large"
                                                        style={{ flex: 1 }}
                                                    />
                                                    <Button
                                                        type="primary"
                                                        size="large"
                                                        loading={googleMapsKeySaving}
                                                        onClick={handleSaveGoogleMapsKey}
                                                        disabled={googleMapsKeyInput === googleMapsKey}
                                                    >
                                                        Save Key
                                                    </Button>
                                                </Space.Compact>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                        </Space>
                    </Card>
                </div>
            ),
        },
        {
            key: 'api',
            label: (
                <Space>
                    <ApiOutlined />
                    <span>API Configuration</span>
                </Space>
            ),
            children: (
                <div>
                    <Row gutter={[24, 24]}>
                        <Col xs={24} lg={12}>
                            <StoreApiKeys />
                        </Col>
                        <Col xs={24} lg={12}>
                            <SignatureTester />
                        </Col>
                    </Row>
                </div>
            ),
        },
    ];

    return (
        <MainLayout>
            {/* Page Header */}
            <div style={{ 
                marginBottom: 32,
                paddingBottom: 24,
                borderBottom: '1px solid #f0f0f0'
            }}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Space align="center" size="middle">
                        <div style={{
                            width: 48,
                            height: 48,
                            borderRadius: 12,
                            background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)'
                        }}>
                            <SettingOutlined style={{ fontSize: 24, color: '#ffffff' }} />
                        </div>
                        <div>
                            <Title level={2} style={{ margin: 0, lineHeight: 1.2 }}>
                                Settings
                            </Title>
                            <Paragraph style={{ margin: 0, fontSize: 14, color: '#595959' }}>
                                Manage your application preferences and API configurations
                            </Paragraph>
                        </div>
                    </Space>
                </Space>
            </div>

            {/* Settings Content */}
            <Card
                variant="borderless"
                style={{
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                }}
            >
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={tabItems}
                    size="large"
                    style={{
                        minHeight: 400
                    }}
                />
            </Card>
        </MainLayout>
    )
}

export default Setting